import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { documentApi } from 'api/main';
import { texts } from 'utils/languages/fr';

import { CustomFormLabel, FileInputButton, FileErrorMessage, InfoButton } from 'components';
import { Box, OutlinedInput, IconButton, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const MultipleDocumentInput = ({
    name,
    documents = [],
    label,
    loading,
    onDocChange,
    onDocRemove,
    onDocReplace,
    deletingDocId,
    replacingDocId,
    fileSizeError,
    error,
    infoButtonMessage = null,
}) => {
    const downloadClick = useCallback((url, docName, mimeType) => {
        const link = document.createElement('a');
        if (
            mimeType !==
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
            mimeType !== 'application/vnd.openxmlformats-officedocument.word'
        ) {
            link.target = '_blank';
        } else {
            link.download = docName;
        }
        link.href = url;
        link.click();
    }, []);

    const handleDownload = useCallback(
        docId => {
            if (docId) {
                const doc = documents.find(d => d.id === docId);
                documentApi
                    .downloadDocumentById(docId)
                    .then(response => {
                        const bytes = new Uint8Array(response.data);
                        const blob = new Blob([bytes], { type: doc?.mimeType });
                        const url = window.URL.createObjectURL(blob);
                        downloadClick(url, doc?.name, doc?.mimeType);
                    })
                    .catch(() => {
                        console.log('error downloading document');
                    });
            }
        },
        [documents, downloadClick]
    );

    const handleRemove = useCallback(
        docId => {
            onDocRemove(docId);
        },
        [onDocRemove]
    );

    const handleReplace = useCallback(
        (docId, event) => {
            const file = event.target.files[0];
            if (file && onDocReplace) {
                onDocReplace(docId, file);
            }
            // Reset the input value to allow selecting the same file again
            event.target.value = '';
        },
        [onDocReplace]
    );

    const triggerFileInput = useCallback(
        docId => {
            const fileInput = document.getElementById(`replace-${name}-${docId}`);
            if (fileInput) {
                fileInput.click();
            }
        },
        [name]
    );

    return (
        <Box width="100%">
            {label && <CustomFormLabel error={!!error}>{label}</CustomFormLabel>}

            <Box display="flex" justifyContent="space-between" width="100%">
                {/* Display existing documents */}
                {documents.length > 0 && (
                    <Box flex="1 1">
                        {documents.map((doc, index) => (
                            <Box
                                key={doc.id || index}
                                sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}
                            >
                                <OutlinedInput
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            '&:hover': {
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                            },
                                        },
                                        '@media (max-width: 375px)': {
                                            marginRight: 0,
                                            marginBottom: 0.625,
                                        },
                                    }}
                                    fullWidth
                                    disabled
                                    value={doc.name || `Document ${index + 1}`}
                                    onClick={() => handleDownload(doc.id)}
                                    size="small"
                                    error={!!error}
                                />
                                <IconButton
                                    sx={{ p: 0.5, color: 'primary.main' }}
                                    onClick={() => triggerFileInput(doc.id)}
                                    size="small"
                                    disabled={deletingDocId === doc.id || replacingDocId === doc.id}
                                >
                                    {replacingDocId === doc.id ? (
                                        <icons.Loading fontSize="small" />
                                    ) : (
                                        <icons.FileReplace fontSize="small" />
                                    )}
                                </IconButton>
                                <IconButton
                                    sx={{ p: 0.5, color: 'error.main' }}
                                    onClick={() => handleRemove(doc.id)}
                                    size="small"
                                    disabled={deletingDocId === doc.id || replacingDocId === doc.id}
                                >
                                    {deletingDocId === doc.id ? (
                                        <icons.Loading fontSize="small" />
                                    ) : (
                                        <icons.Delete fontSize="small" />
                                    )}
                                </IconButton>
                                {/* Hidden file input for replace functionality */}
                                <input
                                    type="file"
                                    id={`replace-${name}-${doc.id}`}
                                    style={{ display: 'none' }}
                                    accept=".pdf,.docx,.doc"
                                    onChange={e => handleReplace(doc.id, e)}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Add new document section */}
                <Box
                    sx={{
                        flex: documents.length > 0 ? '0 1 100px' : '1 1',
                        alignSelf: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1,
                            '@media (max-width: 375px)': {
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            },
                        }}
                    >
                        <Box
                            display="flex"
                            flexDirection="row"
                            flexWrap="nowrap"
                            flexGrow="1"
                            gap={2}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            {documents.length === 0 && (
                                <Typography
                                    variant="body2"
                                    color={error ? 'error' : 'textSecondary'}
                                >
                                    {texts.NO_DOCUMENTS}
                                </Typography>
                            )}
                            {infoButtonMessage && (
                                <InfoButton
                                    disableMarginBottom
                                    title={label}
                                    content={infoButtonMessage}
                                />
                            )}
                        </Box>
                        <FileInputButton
                            id={name}
                            loading={loading}
                            label={texts.UPLOAD_FILE_LABEL}
                            onFileChange={onDocChange}
                            sx={{ mb: '0 !important' }}
                            acceptedFileTypes=".pdf, .docx, .doc"
                            multiple
                        />
                    </Box>
                    {fileSizeError === name && <FileErrorMessage variant="size" />}
                </Box>
            </Box>
        </Box>
    );
};

MultipleDocumentInput.propTypes = {
    name: PropTypes.string.isRequired,
    documents: PropTypes.array,
    label: PropTypes.string,
    loading: PropTypes.bool,
    onDocChange: PropTypes.func.isRequired,
    onDocRemove: PropTypes.func.isRequired,
    onDocReplace: PropTypes.func,
    deletingDocId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    replacingDocId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fileSizeError: PropTypes.string,
    error: PropTypes.bool,
    infoButtonMessage: PropTypes.node,
};

export default MultipleDocumentInput;
